import { by, Order } from '@hyldmo/by'
import type { AbsoluteMacros, Recipe } from '@macromaxxing/db'
import { Import, Package, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button, Card, Select, Spinner, TRPCError } from '~/components/ui'
import { cn, useDocumentTitle, useUser } from '~/lib'
import { trpc } from '~/lib/trpc'
import { PremadeDialog } from './components/PremadeDialog'
import { RecipeCard } from './components/RecipeCard'
import { RecipeImportDialog } from './components/RecipeImportDialog'
import {
	calculatePortionMacros,
	calculateRecipeTotals,
	getEffectiveCookedWeight,
	type IngredientWithAmount,
	toIngredientWithAmount
} from './utils/macros'

type Filter = 'all' | 'mine' | 'premade'

type Sort = 'recent' | keyof AbsoluteMacros | keyof Recipe
const sortOptions: Sort[] = ['recent', 'protein', 'carbs', 'fat', 'kcal', 'fiber', 'name']

export function RecipeListPage() {
	useDocumentTitle('Recipes')
	const [filter, setFilter] = useState<Filter>('all')
	const [sort, setSort] = useState<Sort>('recent')
	const [showImport, setShowImport] = useState(false)
	const [showPremade, setShowPremade] = useState(false)
	const navigate = useNavigate()
	const { user } = useUser()
	const recipesQuery = trpc.recipe.list.useQuery()

	const recipesWithMacros = useMemo(() => {
		if (!recipesQuery.data) return []
		return recipesQuery.data.map(recipe => {
			const items: IngredientWithAmount[] = recipe.recipeIngredients.map(toIngredientWithAmount)
			const totals = calculateRecipeTotals(items)
			const cookedWeight = getEffectiveCookedWeight(totals.weight, recipe.cookedWeight)
			const portion = calculatePortionMacros(totals, cookedWeight, recipe.portionSize)
			return { recipe, portion, isMine: recipe.userId === user?.id }
		})
	}, [recipesQuery.data, user?.id])

	const sortedRecipes = useMemo(() => {
		const filtered = recipesWithMacros.filter(r => {
			if (filter === 'mine') return r.isMine && r.recipe.type !== 'premade'
			if (filter === 'premade') return r.isMine && r.recipe.type === 'premade'
			return r.recipe.type !== 'premade'
		})
		return filtered.toSorted(
			by(v => {
				if (sort in v.portion) return v.portion[sort as keyof typeof v.portion]
				if (sort in v.recipe) return v.recipe[sort as keyof typeof v.recipe]
				return v.recipe.createdAt
			}, Order.Desc)
		)
	}, [recipesWithMacros, filter, sort])

	const myRecipeCount = recipesWithMacros.filter(r => r.isMine && r.recipe.type !== 'premade').length
	const premadeCount = recipesWithMacros.filter(r => r.isMine && r.recipe.type === 'premade').length

	return (
		<div className="space-y-3">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<div className="flex flex-wrap items-center gap-2">
					<h1 className="font-semibold text-ink">Recipes</h1>
					{user && (
						<div className="flex gap-1">
							<button
								type="button"
								onClick={() => setFilter('all')}
								className={cn(
									'rounded-full px-2.5 py-0.5 text-xs transition-colors',
									filter === 'all'
										? 'bg-accent text-white'
										: 'bg-surface-2 text-ink-muted hover:text-ink'
								)}
							>
								All
							</button>
							<button
								type="button"
								onClick={() => setFilter('mine')}
								className={cn(
									'rounded-full px-2.5 py-0.5 text-xs transition-colors',
									filter === 'mine'
										? 'bg-accent text-white'
										: 'bg-surface-2 text-ink-muted hover:text-ink'
								)}
							>
								Mine{myRecipeCount > 0 && ` (${myRecipeCount})`}
							</button>
							<button
								type="button"
								onClick={() => setFilter('premade')}
								className={cn(
									'rounded-full px-2.5 py-0.5 text-xs transition-colors',
									filter === 'premade'
										? 'bg-accent text-white'
										: 'bg-surface-2 text-ink-muted hover:text-ink'
								)}
							>
								Premade{premadeCount > 0 && ` (${premadeCount})`}
							</button>
						</div>
					)}
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<Select className="w-auto" value={sort} onChange={setSort} options={sortOptions} />
					{user && (
						<>
							<Button variant="outline" onClick={() => setShowImport(true)}>
								<Import className="size-4" />
								Import
							</Button>
							<Button variant="outline" onClick={() => setShowPremade(true)}>
								<Package className="size-4" />
								Premade
							</Button>
							<Link to="/recipes/new">
								<Button>
									<Plus className="size-4" />
									New Recipe
								</Button>
							</Link>
						</>
					)}
				</div>
			</div>

			{recipesQuery.isLoading && (
				<div className="flex justify-center py-12">
					<Spinner />
				</div>
			)}

			{recipesQuery.error && <TRPCError error={recipesQuery.error} />}

			{sortedRecipes.length === 0 && !recipesQuery.isLoading && (
				<Card className="py-12 text-center text-ink-faint">
					{filter === 'premade'
						? "You haven't added any premade meals yet."
						: filter === 'mine'
							? "You haven't created any recipes yet."
							: 'No recipes yet. Create the first one!'}
				</Card>
			)}

			<div className="grid gap-2">
				{sortedRecipes.map(({ recipe, portion, isMine }) => (
					<RecipeCard key={recipe.id} recipe={recipe} portion={portion} isMine={isMine} />
				))}
			</div>
			<RecipeImportDialog open={showImport} onClose={() => setShowImport(false)} />
			<PremadeDialog
				open={showPremade}
				onClose={() => setShowPremade(false)}
				onCreated={recipe => navigate(`/recipes/${recipe.id}`)}
			/>
		</div>
	)
}
